import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  invoiceSortFields,
  type Invoice,
  type InvoiceListQuery,
  type UnsettledCorrection,
} from '@faktorplus/shared';
import {
  fetchAllInvoices,
  useAllInvoices,
  useSettleCorrection,
  useUnsettledCorrections,
} from '../../api/invoices';
import { InvoiceStateBadge, invoiceRowTone } from '../../components/ui/status-pill';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { SortableTableHeader } from '../../components/ui/sortable-table-header';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { CoinsIcon, FileIcon, ShieldIcon, WalletIcon } from '../../components/ui/icons';
import {
  hasAnyInvoiceFilter,
  InvoiceFilters,
  readInvoiceFilters,
} from '../invoices/invoice-filters';
import { exportInvoicesToCsv } from '../invoices/export-invoices';
import { AttachmentQuickView } from '../invoices/attachment-preview';
import { ApiError } from '../../lib/api-client';
import { formatAmount, formatMoney, formatMoneyCompact, formatShortDate } from '../../lib/format';
import styles from '../../components/layout/page.module.css';

const ROW_TONE_CLASS = {
  late: styles.rowLate,
  cash: styles.rowCash,
  none: undefined,
} as const;

/**
 * Payables — the equivalent of ROZRACHUNKI.xlsx, with the things the
 * spreadsheet never did:
 *
 *  - "Zatrzymanie": warranty retention, so the amount transferred is below gross;
 *  - the scan one click away, because deciding what to pay keeps needing it;
 *  - cash invoices left out entirely: they were settled at the counter and were
 *    never something the company owes.
 *
 * The whole statement at once, no pages (24.09.2026): unpaid invoices are few
 * enough, and the client reads this list top to bottom as "co jesteśmy komu
 * winni". Default order follows the view: by vendor for the open items, by
 * receive date (oldest first) when the settled ones are shown too — that is
 * how the paper folder is kept.
 */
export function PayablesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  // "Pokaż wszystkie": settled invoices join the list, visibly greyed out.
  // Cash stays excluded either way — it was never a rozrachunek.
  const showSettled = searchParams.get('all') === 'true';
  const sortBy = readSortBy(searchParams.get('sortBy'), showSettled);
  const sortDir = readSortDirection(searchParams.get('sortDir'));

  const filters = {
    ...readInvoiceFilters(searchParams),
    ...(showSettled ? {} : { onlyUnpaid: true as const }),
    excludeCash: true as const,
  };
  const { data, isLoading, error } = useAllInvoices({ ...filters, sortBy, sortDir });
  // Open correction balances belong to what is owed — "żeby nam to nie
  // umknęło" (24.09.2026). Narrowed by the vendor filter alongside the table.
  const { data: allCorrections = [] } = useUnsettledCorrections();

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać rozrachunków.</ErrorBox>;

  const invoices = data?.items ?? [];
  const corrections = filters.vendorId
    ? allCorrections.filter((correction) => correction.vendorId === filters.vendorId)
    : allCorrections;
  // The tiles always describe what is *owed*, whichever view is open — with
  // the settled rows shown, summing their gross into "Do zapłaty" would
  // inflate the one number this screen exists to answer.
  const unpaid = invoices.filter((invoice) => invoice.paidAt === null);
  const overdue = unpaid.filter((invoice) => invoice.isOverdue);
  const totals = sumUnpaid(unpaid, corrections);

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }

    // The default ordering belongs to the view, so switching the settled
    // range drops any explicit sort instead of carrying it across views.
    if ('all' in patch && (patch.all === 'true') !== showSettled) {
      next.delete('sortBy');
      next.delete('sortDir');
    }

    setSearchParams(next, { replace: true });
  }

  function sort(column: string) {
    const nextSortBy = column as InvoiceListQuery['sortBy'];
    const nextSortDir = sortBy === nextSortBy && sortDir === 'asc' ? 'desc' : 'asc';
    update({ sortBy: nextSortBy, sortDir: nextSortDir });
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      exportInvoicesToCsv((await fetchAllInvoices({ ...filters, sortBy, sortDir })).items);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Rozrachunki</h1>
        <div className={styles.actions}>
          <Button
            onClick={() => void handleExport()}
            isLoading={isExporting}
            disabled={invoices.length === 0}
          >
            ↓ Eksport do Excela
          </Button>
        </div>
      </header>

      {unpaid.length > 0 && (
        <StatTiles>
          <StatTile
            icon={<FileIcon />}
            label="Niezapłaconych"
            value={String(unpaid.length)}
            note={overdue.length > 0 ? `${overdue.length} po terminie` : undefined}
          />
          <StatTile
            icon={<CoinsIcon />}
            label="Brutto"
            value={formatMoneyCompact(totals.grossAmount)}
            note={formatMoney(totals.grossAmount)}
          />
          <StatTile
            icon={<ShieldIcon />}
            label="Zatrzymanie"
            value={formatMoneyCompact(totals.retentionAmount)}
            note={formatMoney(totals.retentionAmount)}
          />
          <StatTile
            tone="accent"
            icon={<WalletIcon />}
            label="Do zapłaty"
            value={formatMoneyCompact(totals.amountDue)}
            note={formatMoney(totals.amountDue)}
          />
        </StatTiles>
      )}

      <InvoiceFilters
        params={searchParams}
        onChange={update}
        onReset={() => setSearchParams({}, { replace: true })}
        title="Filtry rozrachunków"
        showUnpaidFilter={false}
        showSettledFilter
        showArchivedFilter={false}
      />

      {overdue.length > 0 && <OverdueBanner invoices={overdue} />}

      {corrections.length > 0 && <UnsettledCorrections corrections={corrections} />}

      {invoices.length === 0 ? (
        <EmptyState
          title="Brak zobowiązań"
          description={
            hasAnyInvoiceFilter(searchParams)
              ? 'Nic nie pasuje do filtrów. Zmień je albo wyczyść.'
              : 'Wszystkie faktury są rozliczone.'
          }
        />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <SortableTableHeader label="Kontrahent" column="vendorName" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Numer" column="number" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Wpływ" column="receivedDate" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Termin" column="dueDate" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Brutto" column="grossAmount" activeColumn={sortBy} direction={sortDir} onSort={sort} className={styles.numeric} />
                <th className={styles.numeric}>Zatrzymanie</th>
                <th className={styles.numeric}>Do zapłaty</th>
                <SortableTableHeader label="Data zapłaty" column="paidAt" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <th className={styles.numeric}>Dni</th>
                <SortableTableHeader label="Stan" column="status" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  onClick={() => void navigate(`/faktury/${invoice.id}`)}
                  className={rowClass(invoice)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={styles.strong}>
                    {/* One click narrows the table and its totals to this vendor —
                        the "ile wiszę Kowalskiemu" question without the filter dialog. */}
                    <button
                      type="button"
                      className={styles.cellLink}
                      title={`Pokaż tylko ${invoice.vendorName}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        update({ vendorId: invoice.vendorId });
                      }}
                    >
                      {invoice.vendorName}
                    </button>
                  </td>
                  <td className={styles.docNumber}>{invoice.number}</td>
                  <td className={styles.muted}>{formatShortDate(invoice.receivedDate)}</td>
                  <td>{formatShortDate(invoice.dueDate)}</td>
                  <td className={styles.numeric}>{formatAmount(invoice.grossAmount)}</td>
                  <td className={`${styles.numeric} ${invoice.retentionAmount ? '' : styles.muted}`}>
                    {invoice.retentionAmount ? formatAmount(invoice.retentionAmount) : '—'}
                  </td>
                  <td className={`${styles.numeric} ${styles.strong}`}>
                    {formatAmount(invoice.remainingAmount)}
                  </td>
                  <td className={styles.muted}>{formatShortDate(invoice.paidAt)}</td>
                  <td className={`${styles.numeric} ${daysClass(invoice)}`}>
                    {formatDays(invoice)}
                  </td>
                  <td>
                    <InvoiceStateBadge invoice={invoice} />
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      {invoice.attachments[0] && (
                        <AttachmentQuickView
                          attachment={invoice.attachments[0]}
                          label={invoice.number}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function rowClass(invoice: Invoice): string | undefined {
  if (invoice.paidAt !== null) return styles.rowSettled;
  return ROW_TONE_CLASS[invoiceRowTone(invoice) ?? 'none'];
}

/**
 * Sums in integer grosze: the amounts arrive with two decimals, and adding a
 * few hundred floats head-on is exactly how 0,01 zł discrepancies are born.
 */
function sumUnpaid(unpaid: Invoice[], corrections: UnsettledCorrection[]) {
  const cents = (value: number) => Math.round(value * 100);
  const total = (pick: (invoice: Invoice) => number) =>
    unpaid.reduce((sum, invoice) => sum + cents(pick(invoice)), 0) / 100;

  // Open correction balances move what is owed, both ways — a lowering
  // correction to a paid invoice means the vendor owes the company.
  const correctionBalance =
    corrections.reduce((sum, correction) => sum + cents(correction.deltaAmount), 0) / 100;

  return {
    grossAmount: total((invoice) => invoice.grossAmount),
    retentionAmount: total((invoice) => invoice.retentionAmount),
    // Partial payments already made are not owed any more (24.09.2026).
    amountDue:
      Math.round((total((invoice) => invoice.remainingAmount) + correctionBalance) * 100) /
      100,
  };
}

/**
 * The vendor balances the spreadsheet used to carry "na minus": corrections
 * whose delta is not accounted for yet. Each row leads to the correction —
 * the full settle-against-an-invoice picker lives there — and "Rozliczono"
 * closes the balance by hand right here.
 */
function UnsettledCorrections({ corrections }: { corrections: UnsettledCorrection[] }) {
  const settle = useSettleCorrection();

  return (
    <Panel title={`Korekty nierozliczone — ${corrections.length}`}>
      {settle.error && (
        <ErrorBox>
          {settle.error instanceof ApiError
            ? settle.error.message
            : 'Nie udało się rozliczyć korekty.'}
        </ErrorBox>
      )}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Kontrahent</th>
              <th>Korekta</th>
              <th>Koryguje</th>
              <th className={styles.numeric}>Saldo</th>
              <th>Z dnia</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {corrections.map((correction) => (
              <tr key={correction.id}>
                <td className={styles.strong}>{correction.vendorName}</td>
                <td className={styles.docNumber}>
                  <Link to={`/faktury/${correction.id}`} className={styles.cellLink}>
                    {correction.number}
                  </Link>
                </td>
                <td className={styles.muted}>
                  <Link
                    to={`/faktury/${correction.correctsInvoiceId}`}
                    className={styles.cellLink}
                  >
                    {correction.correctsNumber}
                  </Link>
                </td>
                <td className={`${styles.numeric} ${styles.strong}`}>
                  {formatAmount(correction.deltaAmount)}
                </td>
                <td className={styles.muted}>{formatShortDate(correction.issueDate)}</td>
                <td>
                  <div className={styles.rowActions}>
                    <Link to={`/faktury/${correction.id}`}>
                      <Button>Rozlicz na fakturze…</Button>
                    </Link>
                    <Button
                      isLoading={
                        settle.isPending && settle.variables?.id === correction.id
                      }
                      onClick={() =>
                        settle.mutate({ id: correction.id, input: { targetInvoiceId: null } })
                      }
                    >
                      Oznacz jako rozliczoną
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function OverdueBanner({ invoices }: { invoices: Invoice[] }) {
  const worst = invoices.reduce((latest, invoice) =>
    (invoice.daysUntilDue ?? 0) < (latest.daysUntilDue ?? 0) ? invoice : latest,
  );

  return (
    <div className={styles.banner} role="alert">
      <span aria-hidden="true">⚠</span>
      <span>
        <b>
          {invoices.length === 1
            ? 'Jedna faktura po terminie.'
            : `${invoices.length} faktury po terminie.`}
        </b>{' '}
        Najdłużej {worst.vendorName} — {Math.abs(worst.daysUntilDue ?? 0)} dni zwłoki.
      </span>
    </div>
  );
}

function formatDays(invoice: Invoice): string {
  if (invoice.daysUntilDue === null) return '—';
  return String(invoice.daysUntilDue);
}

function daysClass(invoice: Invoice): string {
  if (invoice.daysUntilDue === null) return styles.muted!;
  if (invoice.daysUntilDue < 0) return styles.critical!;
  if (invoice.daysUntilDue <= 7) return styles.warning!;
  return '';
}

/**
 * Each view has the ordering the client asked for on 24.09.2026: open items
 * grouped by vendor, the full history chronologically, oldest first.
 */
function readSortBy(value: string | null, showSettled: boolean): InvoiceListQuery['sortBy'] {
  const fallback = showSettled ? 'receivedDate' : 'vendorName';
  return invoiceSortFields.find((field) => field === value) ?? fallback;
}

function readSortDirection(value: string | null): 'asc' | 'desc' {
  return value === 'desc' ? 'desc' : 'asc';
}
